import {
    Component,
    input,
    output,
    ElementRef,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    NgZone,
    inject,
} from '@angular/core'
@Component({
    selector: 'tw-autocomplete',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(document:click)': 'onDocumentClick($event)',
    },
    template: `
        @if (isOpen) {
            <div
                class="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md"
                role="listbox"
                (mousedown)="$event.preventDefault()"
            >
                <ng-content></ng-content>
                @if (showEmpty()) {
                    <div class="py-6 text-center text-sm text-muted-foreground">
                        No results found.
                    </div>
                }
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: block;
                position: relative;
            }
        `,
    ],
})
export class TwAutocompleteComponent {
    showEmpty = input(false)
    optionSelected = output<{ option: { value: unknown } }>()

    isOpen = false
    private closeTimer: ReturnType<typeof setTimeout> | null = null

    private el = inject(ElementRef)
    private cdr = inject(ChangeDetectorRef)
    private zone = inject(NgZone)

    onDocumentClick(event: Event) {
        const target = event.target as HTMLElement | null
        if (target && this.isOpen && !this.el.nativeElement.parentElement?.contains(target)) {
            this.close()
        }
    }

    open() {
        if (this.closeTimer) {
            clearTimeout(this.closeTimer)
            this.closeTimer = null
        }
        this.isOpen = true
        this.cdr.markForCheck()
    }

    close() {
        this.isOpen = false
        this.cdr.markForCheck()
    }

    delayedClose(ms = 200) {
        this.closeTimer = setTimeout(() => this.close(), ms)
    }

    toggle() {
        if (this.isOpen) {
            this.close()
        } else {
            this.open()
        }
    }

    selectOption(value: unknown) {
        this.optionSelected.emit({ option: { value } })
        this.close()
    }
}

@Component({
    selector: 'tw-autocomplete-option',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div
            class="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
            [class.opacity-50]="disabled()"
            [class.pointer-events-none]="disabled()"
            [attr.aria-selected]="selected()"
            role="option"
            tabindex="0"
            (click)="onSelect()"
            (keydown.enter)="onSelect()"
            (keydown.space)="$event.preventDefault(); onSelect()"
        >
            <ng-content></ng-content>
        </div>
    `,
})
export class TwAutocompleteOptionComponent {
    value = input<unknown>()
    disabled = input(false)
    selected = input<boolean>(false)

    private autocomplete = inject(TwAutocompleteComponent)

    onSelect() {
        if (!this.disabled()) {
            this.autocomplete.selectOption(this.value())
        }
    }
}
